import React, { createContext, useCallback, useContext, useState } from "react";
import { Form, message } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { getTraineeToTrainerMap } from "@fitness-app/app-store/src/store/reducers/team/selectors";
import { UserRole } from "@fitness-app/data-models";
import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import { ClientNoteCategory, ClientNoteType, type ClientNote } from "@fitness-app/data-models/entities/ClientNote";
import { TeamMemberTaskCategory } from "@fitness-app/data-models/entities/TeamMemberTask";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { ScheduledMessagesModal } from "~/modules/Chat/components/ScheduledMessages/ScheduledMessagesModal";
import TeamMemberTaskForm from "~/modules/Dashboard/TeamMemberTasks/components/TeamMemberTaskForm";
import { useCanRouteTaskToClientTrainer } from "~/modules/Dashboard/TeamMemberTasks/hooks/useCanRouteTaskToClientTrainer";
import { useCreateTeamMemberTask } from "~/modules/Dashboard/TeamMemberTasks/hooks/useCreateTeamMemberTask";
import { type TeamMemberTaskFormModel, type TraineeOption } from "~/modules/Dashboard/TeamMemberTasks/types";
import {
  SupportRequestFormModal,
  type SupportRequestPrefill,
} from "~/modules/Support/components/SupportRequestFormModal";
import { SUPPORT_BOARD_FEATURE_FLAG } from "~/modules/Support/supportFeatureFlag";
import ClientNoteForm from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/ClientNoteForm";
import { type ClientNoteFormModel } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/types";
import { useAddTraineeNote } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/useTraineeNotes";
import { analyticsService, useAppSelector } from "~/store/initializeStore";

interface TaskModelInput {
  traineeId: string;
  trainee: TraineeOption;
  title: string;
  description?: string;
}

interface ChatUtilitiesProviderContextProps {
  setNoteModel: (model: {
    traineeId: string;
    content: string;
    attachments?: { url: string; name: string; uid: string }[];
  }) => void;
  setTaskModel: (model: TaskModelInput) => void;
  // null when the support board feature flag is off — the caller hides the menu item.
  setSupportRequestModel: ((model: SupportRequestPrefill) => void) | null;
  openScheduledMessages: (traineeId: string) => void;
}

const ChatUtilitiesContext = createContext<ChatUtilitiesProviderContextProps | undefined>(undefined);

export const useChatUtilitiesContext = () => {
  const context = useContext(ChatUtilitiesContext);
  if (!context) {
    throw new Error("useChatUtilitiesContext must be used within a ChatUtilitiesContext");
  }
  return context;
};

export const ChatUtilitiesProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [noteModel, setModel] = useState<(Partial<ClientNoteFormModel> & { traineeId: string }) | null>(null);
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const [taskModel, setTaskModelState] = useState<(Partial<TeamMemberTaskFormModel> & { traineeId: string }) | null>(
    null,
  );
  const [taskForm] = Form.useForm<TeamMemberTaskFormModel>();
  const createTaskMutation = useCreateTeamMemberTask();
  const { role, userId } = useUserRole();
  const { memberId } = useUserClaims();
  const canRouteToClientTrainer = useCanRouteTaskToClientTrainer();
  const traineeToTrainerMap = useAppSelector(getTraineeToTrainerMap);
  const { t } = useTranslation("trainees");
  const addNoteMutation = useAddTraineeNote();
  const [scheduledMessageForTrainee, setScheduledMessageForTrainee] = useState<string | null>(null);
  const { enabled: isSupportBoardEnabled } = usePostHogFeatureFlag(SUPPORT_BOARD_FEATURE_FLAG);
  const [supportRequestModel, setSupportRequestModelState] = useState<SupportRequestPrefill | null>(null);

  const addNote = useCallback(
    async (formData: ClientNoteFormModel) => {
      if (noteModel?.traineeId) {
        const model: ClientNote = {
          attachments: formData.attachments || [],
          id: generateUniqId(),
          traineeId: noteModel.traineeId,
          content: formData.content,
          type: formData.type || ClientNoteType.Text,
          category: formData.category || ClientNoteCategory.General,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          pinned: false,
          authorType: role === UserRole.TRAINER ? AuthorTypeEnum.Admin : AuthorTypeEnum.Trainer,
          authorId: userId,
          showInDashboard: formData.showInDashboard ?? false,
        };

        try {
          await addNoteMutation.mutateAsync(model);
        } catch {
          void message.error("Nie udało się dodać notatki. Spróbuj ponownie.");
          return;
        }
        void analyticsService.track("add_client_note_from_chat");
        setIsOpen(false);
        setTimeout(() => {
          setModel(null);
        }, 300);
      }
    },
    [noteModel, addNoteMutation],
  );

  const setNoteModel = useCallback(
    (model: { traineeId: string; content: string; attachments?: { url: string; name: string; uid: string }[] }) => {
      setModel(model);
      setIsOpen(true);
    },
    [],
  );

  const setTaskModel = useCallback((model: TaskModelInput) => {
    setTaskModelState({
      traineeId: model.traineeId,
      trainee: model.trainee,
      title: model.title,
      description: model.description,
      category: TeamMemberTaskCategory.ClientCommunication,
      dueDate: { date: dayjs().format("YYYY-MM-DD"), time: null, timezone: "Europe/Warsaw" },
    });
    setIsTaskOpen(true);
  }, []);

  const handleTaskSubmit = useCallback(
    async (formData: TeamMemberTaskFormModel) => {
      if (taskModel?.traineeId) {
        const assignedTeamMemberId = canRouteToClientTrainer ? traineeToTrainerMap[taskModel.traineeId] : memberId;
        await createTaskMutation.mutateAsync({
          ...formData,
          traineeId: taskModel.traineeId,
          assignedTeamMemberId: assignedTeamMemberId || null,
        });
        void analyticsService.track("team_member_task_created_from_chat");
        setIsTaskOpen(false);
        setTimeout(() => {
          setTaskModelState(null);
          taskForm.resetFields();
        }, 300);
      }
    },
    [taskModel, createTaskMutation, taskForm, canRouteToClientTrainer, memberId, traineeToTrainerMap],
  );

  const setSupportRequestModel = useCallback((model: SupportRequestPrefill) => {
    setSupportRequestModelState(model);
  }, []);

  return (
    <ChatUtilitiesContext.Provider
      value={{
        setNoteModel,
        setTaskModel,
        setSupportRequestModel: isSupportBoardEnabled ? setSupportRequestModel : null,
        openScheduledMessages: setScheduledMessageForTrainee,
      }}
    >
      {children}
      {noteModel && (
        <ModalForm
          open={isOpen}
          loading={addNoteMutation.isLoading}
          onCancel={() => {
            setIsOpen(false);
            setTimeout(() => {
              setModel(null);
            }, 300);
          }}
          title={t("notes.formTitle")}
        >
          <ClientNoteForm onSubmit={addNote} model={noteModel} traineeId={noteModel.traineeId || "default"} />
        </ModalForm>
      )}
      {taskModel && (
        <ModalForm
          open={isTaskOpen}
          loading={createTaskMutation.isLoading}
          onCancel={() => {
            setIsTaskOpen(false);
            setTimeout(() => {
              setTaskModelState(null);
              taskForm.resetFields();
            }, 300);
          }}
          title="Utwórz zadanie"
        >
          <TeamMemberTaskForm formController={taskForm} onSubmit={handleTaskSubmit} model={taskModel} />
        </ModalForm>
      )}
      {supportRequestModel && (
        <SupportRequestFormModal open prefill={supportRequestModel} onClose={() => setSupportRequestModelState(null)} />
      )}
      {scheduledMessageForTrainee && (
        <ScheduledMessagesModal
          traineeId={scheduledMessageForTrainee}
          onClose={() => setScheduledMessageForTrainee(null)}
        />
      )}
    </ChatUtilitiesContext.Provider>
  );
};
